/**
 * utils/persons.js
 * Pure functions for managing the persons collection (Person/Role restructure — Stage 1 added
 * the data layer; Stage 3 batch 1 added a free-text `note` and the collision guard below; Stage
 * 4 moved `code` here from the role).
 * `data.persons`: { id, name, note, code, createdAt }[] — a person is a human being, plus an
 * optional disambiguating note, plus the ONE code that identifies them on every invoice they
 * ever mint, anywhere they sell. No branch, no commission, no role-of-any-kind lives here —
 * those live on a ROLE (the existing `data.salespeople` collection, which keeps its old name
 * for call-site reasons — see salespeople.js's own header comment). One person may hold several
 * roles (e.g. a Pusat role plus one or more Cabang roles), each a separate record in
 * `data.salespeople` sharing this person's id via `salesperson.personId` — and, as of Stage 4,
 * sharing this ONE code too: the code identifies the PERSON, not where they happen to be
 * selling today.
 *
 * **`code` (Stage 4, moved from the role)**: 1-4 uppercase letters/digits, GLOBALLY unique
 * across every person — this is what a minted CABANG invoice number's counter is keyed on
 * (`mintCabangNumber`, resolved via `salespeople.js`'s `resolveSalespersonCode`), so two people
 * sharing a code would share a counter and, eventually, an invoice-number collision. Required on
 * every person from the moment they're created — a role's own creation flow always supplies one
 * for a BRAND-NEW person (see `SalespersonFormModal`'s own header comment for why an EXISTING
 * person's second/third role never asks for one again: the code already exists, on the person).
 *
 * Mirrors utils/contacts.js's shape and refcount-guarded delete, with ONE deliberate
 * divergence: this module does NOT dedupe by name. Two people can genuinely share a name (the
 * client's own real data has this) and must stay two distinct records — addPerson always
 * creates a new person for a non-blank name, never merges/no-ops on a collision the way
 * addContact/addBranch do. (Two people can never share a CODE, though — that uniqueness is real
 * identity, not a display convenience the way name-uniqueness would be.) `note` exists so a
 * human can tell two same-named people apart ("Abun tua" / "Abun muda") — it is free text,
 * validated the same way contacts.js's own `notes` field is (trimmed, no other requirement,
 * empty is fine), and it is NEVER read by `detectPersonCollision` below to decide whether a pair
 * is ambiguous — see that function's own header comment for why.
 */
import { generateId } from "./idGenerators";

const CODE_RE = /^[A-Z0-9]{1,4}$/;

function norm(s) {
  return String(s || "").trim();
}
function normCode(s) {
  return norm(s).toUpperCase();
}
function codeCollides(persons, code, excludeId) {
  return persons.some((p) => p.id !== excludeId && p.code === code);
}

/**
 * Add a new person. Same-reference no-op when `fields.name` is blank (after trim) —
 * deliberately NO name-collision check (two people may share a name; see file header) — OR
 * when `fields.code` isn't 1-4 chars of [A-Z0-9] (after uppercasing) OR collides with an
 * existing person's code (case-insensitive via the uppercasing). `fields.note` is optional free
 * text, trimmed, defaulting to "" — never required, never validated beyond that (mirrors
 * contacts.js's `notes` field exactly).
 */
export function addPerson(data, fields) {
  const name = norm(fields?.name);
  if (!name) return data;

  const persons = data.persons || [];
  const code = normCode(fields?.code);
  if (!CODE_RE.test(code)) return data;
  if (codeCollides(persons, code, null)) return data;

  const note = norm(fields?.note);
  const person = { id: generateId(), name, note, code, createdAt: new Date().toISOString() };
  return { ...data, persons: [...persons, person] };
}

/**
 * Update an existing person's name, note, and/or code. Same-reference no-op when `id` doesn't
 * resolve, the new name is blank, or a supplied `code` fails the same format/uniqueness rule
 * `addPerson` enforces (excluding the person's own current code — renaming "BD" to "bd" on
 * itself is not a collision). No name-collision check, for the same reason `addPerson` has
 * none. `fields.note`/`fields.code` follow the same present-vs-absent convention as `name`
 * (`!= null` means "the caller supplied this") — but unlike `name`/`code`, an empty note is
 * valid (clearing it is a legitimate edit, not a rejection).
 *
 * **This is the ONE write path a role's code ever changes through (Stage 4)** — a role has no
 * code of its own anymore, so correcting "a salesperson's code" always means calling this
 * function on their linked person; `salespeople.js`'s `updateSalesperson` delegates its own
 * `fields.code` handling here, exactly the way it already delegates `fields.name` — see that
 * function's own header comment. Every one of the person's roles resolves the SAME new code the
 * instant this returns, since none of them store a copy.
 */
export function updatePerson(data, id, fields) {
  const persons = data.persons || [];
  const idx = persons.findIndex((p) => p.id === id);
  if (idx === -1) return data;

  const name = fields?.name != null ? norm(fields.name) : persons[idx].name;
  if (!name) return data;
  const note = fields?.note != null ? norm(fields.note) : persons[idx].note;

  const code = fields?.code != null ? normCode(fields.code) : persons[idx].code;
  if (!CODE_RE.test(code)) return data;
  if (codeCollides(persons, code, id)) return data;

  const next = { ...persons[idx], name, note, code };
  return { ...data, persons: persons.map((p, i) => (i === idx ? next : p)) };
}

/** Whether a personId resolves against data.persons — used by salespeople.js's own add/update
 *  validation (a role's personId must point at a real person). */
export function personExists(persons, id) {
  return (persons || []).some((p) => p.id === id);
}

/** Number of roles (data.salespeople) referencing this person via personId. Persons are never
 *  referenced from anywhere else — no transaction, contact, or history entry points at a
 *  person directly, only at a role. */
export function personRefCount(data, id) {
  return (data.salespeople || []).filter((s) => s.personId === id).length;
}

/** Delete only when unreferenced by any role (personRefCount === 0) AND not linked from any
 *  contact entry's `personIds` (Contact model redesign) — reads `data.contacts` directly rather
 *  than importing utils/contacts.js, the same way `personRefCount` above reads `data.salespeople`
 *  directly without a second module. Kept as an independent second guard clause rather than
 *  folded into `personRefCount` itself: that function's existing meaning ("how many roles") is
 *  relied on elsewhere (`KelolaSalesModal`'s "Orang Tanpa Peran" section uses
 *  `personRefCount === 0` as a structural "definitely has no roles" fact, independent of
 *  contacts) and widening it here would silently change that. */
export function deletePerson(data, id) {
  if (personRefCount(data, id) > 0) return data;
  if ((data.contacts || []).some((c) => (c.personIds || []).includes(id))) return data;
  const persons = data.persons || [];
  const next = persons.filter((p) => p.id !== id);
  if (next.length === persons.length) return data;
  return { ...data, persons: next };
}

/** Display label for a person: just their name — a person has no code/city/branch to
 *  disambiguate with the way a contact or salesperson picker label does. */
export function displayPerson(p) {
  return p.name;
}

/** Resolve a personId to that person's CURRENT name, or null when missing/unresolvable (a
 *  dangling personId, or simply no persons collection at all). Deliberately not a baked-in
 *  fallback — same "return null, let the caller decide" contract as branches.js's own
 *  resolveBranchName — so a caller never fabricates a name for data it can't actually resolve. */
export function resolvePersonName(persons, personId) {
  const p = (persons || []).find((x) => x.id === personId);
  return p ? p.name : null;
}

/** Resolve a personId to that person's CURRENT note, or null when missing/unresolvable — same
 *  "return null, let the caller decide" contract as resolvePersonName. A resolved person's own
 *  `note` is a string (possibly `""`), never null itself, so the caller's own truthiness check
 *  (`note && ...`) already treats a genuinely blank note as "don't render anything," matching
 *  the "must not clutter when unset" display rule this exists for. */
export function resolvePersonNote(persons, personId) {
  const p = (persons || []).find((x) => x.id === personId);
  return p ? p.note : null;
}

/** Resolve a personId to that person's CURRENT code, or null when missing/unresolvable — same
 *  "return null, let the caller decide" contract as resolvePersonName/resolvePersonNote (Stage
 *  4). The ONLY way to read a role's code, via `salespeople.js`'s `resolveSalespersonCode` — a
 *  role has no code of its own anymore. */
export function resolvePersonCode(persons, personId) {
  const p = (persons || []).find((x) => x.id === personId);
  return p ? p.code : null;
}

function normNameKey(s) {
  // Case/whitespace only ever distinguish two names to a machine, never to the human reading a
  // picker — "Abun", "abun", and " Abun " must all collide with each other for this guard to be
  // any use at all.
  return String(s || "").trim().replace(/\s+/g, " ").toLowerCase();
}

/** The branchIds (null = Pusat) of a person's currently-ACTIVE roles only, as a Set. An
 *  `active !== false` reading (not `=== true`) treats a role with no `active` key at all — a
 *  shape that should not exist post-migration, but this stays defensive rather than crashing —
 *  as active, matching this feature's own "defaults true" contract. Deliberately excludes
 *  inactive roles: a role that's been switched off no longer shows up in any picker, so it
 *  cannot make anyone indistinguishable from anyone else. */
function activeRoleBranchIds(salespeople, personId) {
  return new Set(
    (salespeople || [])
      .filter((s) => s.personId === personId && s.active !== false)
      .map((s) => s.branchId)
  );
}

/** True when `a` and `b` are both non-empty AND one is a subset of the other (exact equality is
 *  the trivial case of this). See detectPersonCollision's own header comment for why subset —
 *  not just exact equality — counts, and why both sides must be non-empty. */
function oneSubsetOfOther(a, b) {
  if (a.size === 0 || b.size === 0) return false;
  const [small, big] = a.size <= b.size ? [a, b] : [b, a];
  for (const x of small) if (!big.has(x)) return false;
  return true;
}

/**
 * DETECTION ONLY — never merges, never concludes two records ARE the same person, and is never
 * used anywhere to establish identity. Names are compared here purely so a human can be warned
 * that two DIFFERENT people might look identical in a picker; this function never treats a name
 * match (with or without a role-set match) as evidence the two records refer to the same real
 * person. Two people are allowed to share a name — see this file's own header comment — this
 * function exists only to flag when they'd ALSO share a role-set, which is the one combination
 * that makes them impossible to tell apart on sight.
 *
 * Answers, for a proposed `change`: would it produce a pair of people whose name AND role-set
 * (the set of branches — null = Pusat — where they hold an ACTIVE role) both match an already-
 * existing, different person? `change` is one of:
 *   { kind: "add-person", name, branchId }  — a brand-new person with a first role at branchId
 *   { kind: "add-role",   personId, branchId } — an additional role for an existing person
 *   { kind: "rename",     personId, name }  — renaming an existing person (role-set unchanged)
 *
 * Returns `{ ambiguous: false }` when nothing would collide, else `{ ambiguous: true, name,
 * branchIds, existing: {id, name}, changed: {id, name} }` — `branchIds` is the shared role-set
 * (what both would show as "where they work"), `existing` is the other, unrelated, already-real
 * person they'd become indistinguishable from, and `changed` is the person as the proposed
 * change would leave them (`id: null` only for `"add-person"`, since that person doesn't exist
 * yet). This is enough for a caller to build a message like "This would look just like {existing
 * .name} ({branchIds}) — are you sure?"
 *
 * Two design decisions, made deliberately rather than left implicit:
 *
 * 1. SUBSET, not just exact equality, counts as a role-set match. A person working only at MMC
 *    and a person working at MMC+AMIN read identically in any context that only ever shows
 *    "where do they work at THIS branch" (which is how every branch-filtered picker in this app
 *    already works) — the extra branch on the larger set doesn't help distinguish them anywhere
 *    the smaller set's own branch is being shown. Exact equality is just the reflexive case of
 *    subset, so implementing subset covers both without a second code path. BOTH sides must be
 *    non-empty, though — the empty set is mathematically a subset of everything, but a person
 *    with zero active roles appears in no picker anywhere, so there is nothing for them to be
 *    confused with regardless of whose name they share (this is also exactly what makes the
 *    "only role is inactive" edge case resolve to "no collision," below).
 * 2. `note` is never read here. Suppressing the warning because one person already has a
 *    distinguishing note would mean this guard's honesty depends on free text nobody is forced
 *    to keep accurate, keep unique, or even fill in — the opposite of "the app says so out loud
 *    rather than quietly choosing one." A note helps a HUMAN reading the picker later; it must
 *    never talk this guard out of warning in the moment the ambiguity is actually created.
 *
 * This function does not validate that a proposed `branchId` resolves to a real `data.branches`
 * entry — that's addSalesperson's job. A branchId that resolves to nothing is still compared as
 * a plain opaque value: two people sharing one, real or not, would still render identically.
 */
export function detectPersonCollision(data, change) {
  const persons = data.persons || [];
  const salespeople = data.salespeople || [];

  let changedId = null;
  let name;
  let branchIds;

  if (change?.kind === "add-person") {
    name = change.name;
    branchIds = new Set([change.branchId]);
  } else if (change?.kind === "add-role") {
    changedId = change.personId;
    name = resolvePersonName(persons, changedId);
    branchIds = activeRoleBranchIds(salespeople, changedId);
    branchIds.add(change.branchId);
  } else if (change?.kind === "rename") {
    changedId = change.personId;
    name = change.name;
    branchIds = activeRoleBranchIds(salespeople, changedId);
  } else {
    return { ambiguous: false };
  }

  const key = normNameKey(name);
  if (!key) return { ambiguous: false };

  for (const other of persons) {
    if (other.id === changedId) continue; // never collide with yourself
    if (normNameKey(other.name) !== key) continue;

    const otherBranchIds = activeRoleBranchIds(salespeople, other.id);
    if (!oneSubsetOfOther(branchIds, otherBranchIds)) continue;

    const shared = Array.from(branchIds).filter((b) => otherBranchIds.has(b));
    return {
      ambiguous: true,
      name,
      branchIds: shared,
      existing: { id: other.id, name: other.name },
      changed: { id: changedId, name },
    };
  }

  return { ambiguous: false };
}
